import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import type { ChildRouteContext } from "@/root";

import ReviewFeedbackList from "@/modules/performance/user/components/review/ReviewFeedbackList";
import ReviewPeriodFeedbacks from "@/modules/performance/user/components/review/ReviewPeriodFeedbacks";
import ReviewPeriodList from "@/modules/performance/user/components/review/ReviewPeriodList";

const tabs = ["inbox", "periods", "feedback", "archive"] as const;

export const Route = createFileRoute(
  "/_panel/_performance/performance/reviews/",
)({
  component: RouteComponent,
  validateSearch: z.object({
    tab: z.enum(tabs).default("inbox"),
    period: z.coerce.number().int().optional(),
    review: z.coerce.number().int().optional(),
    // Inbox "Evaluate" sends this so we open that evaluation, not the self tab.
    feedback: z.coerce.number().int().optional(),
  }),
  beforeLoad: ({ context }) =>
    ({
      crumb: [
        {
          label: context.t("Review Periods", { ns: "performance.user" }),
        },
      ],
    }) satisfies ChildRouteContext,
});

function RouteComponent() {
  const { t } = useTranslation("performance.user");
  const { period, review, feedback, tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <TabGroup
      selectedIndex={tabs.indexOf(tab) || 0}
      onChange={(index) => navigate({ search: { tab: tabs[index] } })}
    >
      <TabList>
        <Tab>{t("Inbox", { ns: "core.shared" })}</Tab>
        <Tab>{t("Review Periods")}</Tab>
        <Tab>{t("Feedback")}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <ReviewFeedbackList />
        </TabPanel>
        <TabPanel>
          <ReviewPeriodList />
        </TabPanel>
        <TabPanel>
          <ReviewPeriodFeedbacks
            periodId={period}
            reviewId={review}
            feedbackId={feedback}
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
}
